import { Locale, mapLocaleToFlagKey } from "../../../../app-settings/localesAndCurrencies";
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  SystemProps,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { setAppCurrency, setAppLocale, useAppSettingsSnapshot } from "../../../settings";
import { useDisclosure } from "@chakra-ui/react";
import { LanguageSwitcher } from "@airhelp/react";
import { currencies, Currency, locales } from "../../../../app-settings/localesAndCurrencies";
import { sendErrorReport } from "utils/reporting";
import FlagBox from "../ui-elements/language-switcher/FlagBox";
const { en, "pt-BR": ptBr, ...restOfAvailableLocales } = locales;
const availableCurrencies = Object.values(currencies);

const modalSections: ModalSectionsConfigurationObject = {
  topSection: {
    title: "footer.settings_modal.suggested_languages",
    items: [en, ptBr],
  },
  middleSection: {
    title: "footer.settings_modal.select_language",
    items: Object.values(restOfAvailableLocales),
    gridParams: { base: "row", lg: "column" },
  },
  footerSection: {
    title: "footer.settings_modal.select_currency",
    items: [
      {
        code: currencies.EUR.code,
        displayName: currencies.EUR.displayName,
        icon: currencies.EUR.icon,
      },
      {
        code: currencies.USD.code,
        displayName: currencies.USD.displayName,
        icon: currencies.USD.icon,
      },
    ],
  },
};

export function SettingsModal() {
  const { isOpen, onOpen: openSettingsModal, onClose: closeSettingModal } = useDisclosure();
  const { t } = useTranslation();
  const { locale, currency } = useAppSettingsSnapshot();

  const lists = Object.values(modalSections).map((section) => {
    return {
      listHeading: t(section.title),
      listItems: section.items.map((item: ModalSectionItem) => {
        return {
          id: item.code,
          name: item.displayName,
          icon: item?.icon,
        };
      }),
      listAutoflow: section?.gridParams,
    };
  });

  const flagKey = mapLocaleToFlagKey(locale);

  return (
    <FlagBox flagKey={flagKey} locale={locale} handleClick={openSettingsModal}>
      <Modal
        data-testid="languageSwitcherModal"
        isOpen={isOpen}
        onClose={closeSettingModal}
        size="4xl"
        isCentered={false}
      >
        <ModalOverlay />
        <ModalContent
          overflow="hidden"
          data-testid="languageSwitcherModalContent"
          maxW={{ md: "fit-content" }}
          maxHeight="calc(100dvh - var(--chakra-space-))"
        >
          <ModalCloseButton
            data-testid="modal-close-button"
            borderRadius="sm"
            boxSize={2}
            sx={{
              svg: { boxSize: 2.5 },
              _hover: { backgroundColor: "primary.200" },
              _active: { backgroundColor: "primary.800", color: "greyscale.200" },
            }}
          />
          <ModalBody
            pb={0}
            pr={2}
            pt={16}
            mr={{ base: 4, md: 8 }}
            mb={{ base: 4, md: 8 }}
            overflowX="hidden"
          >
            <LanguageSwitcher
              lists={lists}
              activeItems={[locale.toLowerCase(), currency]}
              onItemClick={(item) => {
                handleItemClick(item);
                closeSettingModal();
              }}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </FlagBox>
  );
}

function detectCurrencyChange(code: string) {
  return availableCurrencies.some((currency) => currency.code === code);
}

function handleItemClick({ id }: { id: string; name: string; icon?: any }) {
  if (!id) {
    sendErrorReport(new Error("Attempted to change to undefined locale or currency."));
    return;
  }

  if (detectCurrencyChange(id)) {
    setAppCurrency(id as Currency);
    return;
  }

  setAppLocale(id as Locale);
}

type ModalSectionsConfigurationObject = {
  [_p in string]: {
    title: string;
    items: ModalSectionItem[];
    gridParams?: SystemProps["gridAutoFlow"];
  };
};

type ModalSectionItem = {
  code: string;
  displayName: string;
  icon?: any;
};
